import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { Button } from './ui/button';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend,
} from 'recharts';
import { TrendingUp, Eye, RefreshCw } from 'lucide-react';

// ★ 2026-08-11 (Fase 37) — Grafico settimanale visite per resort
// Usato nella Dashboard CRM.
// Fetch: GET /api/public/visits-stats-daily?days=N
// Mostra una linea per ogni slug/resort con Recharts LineChart.

const SLUG_META = {
  'la-mar-residence': { color: '#e11d48', label: 'La Mar Residence', flag: '🇦🇱' },
  'narea':            { color: '#dc2626', label: 'Narea',            flag: '🇦🇱' },
  'lombok':           { color: '#0891b2', label: 'Lombok',           flag: '🇮🇩' },
  'spille':           { color: '#7c3aed', label: 'Spille',           flag: '🇦🇱' },
  'chumphon':         { color: '#f59e0b', label: 'Chumphon',         flag: '🇹🇭' },
};

const CHART_TICK_STYLE = { fontSize: 10, fill: '#6b7280' };

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
  } catch { return iso; }
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const dt = new Date(`${label}T00:00:00`);
  const dateStr = dt.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
  const items = payload
    .filter((p) => p.value > 0 || payload.length <= 3)
    .sort((a, b) => (b.value || 0) - (a.value || 0));
  return (
    <div className="rounded-lg border border-border bg-[var(--surface-1)] p-2.5 shadow-lg text-xs">
      <div className="font-semibold mb-1.5">{dateStr}</div>
      {items.map((p) => {
        const meta = SLUG_META[p.dataKey] || {};
        return (
          <div key={p.dataKey} className="flex items-center gap-2 py-0.5">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
            <span className="text-muted-foreground">{meta.flag} {meta.label || p.dataKey}</span>
            <span className="ml-auto font-semibold">{p.value}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function VisitsWeeklyChart({ defaultDays = 7 }) {
  const { t } = useTranslation();
  const [days, setDays] = useState(defaultDays);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = React.useCallback(() => {
    setLoading(true);
    api.get(`/public/visits-stats-daily?days=${days}`)
      .then((r) => setPayload(r.data))
      .catch(() => setPayload(null))
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    if (!payload?.data) return { total: 0, by_slug: {} };
    const by_slug = {};
    let total = 0;
    for (const row of payload.data) {
      for (const key of Object.keys(row)) {
        if (key === 'date') continue;
        by_slug[key] = (by_slug[key] || 0) + (row[key] || 0);
        total += row[key] || 0;
      }
    }
    return { total, by_slug };
  }, [payload]);

  const hasData = totals.total > 0;
  const slugs = payload?.slugs || [];

  return (
    <Card className="border-border" data-testid="visits-weekly-chart">
      <CardContent className="p-4 md:p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-[hsl(var(--primary))]" />
              <h3 className="font-serif text-lg">{t('visits.title', 'Trend visite landing')}</h3>
              <Badge variant="secondary" className="text-[10px]">{t('visits.lastNDays', 'Ultimi {{n}} giorni', { n: days })}</Badge>
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {t('visits.subtitle', 'Visitatori unici giornalieri per progetto — dati reali dalla landing pubblica')}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {[7, 14, 30].map((d) => (
              <Button
                key={`days-${d}`}
                size="sm"
                variant={days === d ? 'default' : 'outline'}
                className="h-7 text-xs px-2.5"
                onClick={() => setDays(d)}
                data-testid={`visits-chart-days-${d}`}
              >
                {d}gg
              </Button>
            ))}
            <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={load} aria-label={t('common.refresh', 'Aggiorna')}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Totali per slug */}
        {!loading && hasData && (
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {slugs.map((s) => {
              const meta = SLUG_META[s] || {};
              const value = totals.by_slug[s] || 0;
              return (
                <Badge
                  key={s}
                  variant="outline"
                  className="text-[11px] gap-1.5"
                  style={{ borderColor: meta.color, color: meta.color }}
                  data-testid={`visits-total-${s}`}
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.color }} />
                  {meta.flag} {meta.label || s}: <b>{value}</b>
                </Badge>
              );
            })}
            <Badge className="text-[11px] bg-emerald-50 text-emerald-700 border-emerald-300 border gap-1.5">
              <Eye className="w-3 h-3" />
              {t('visits.total', 'Totale:')} <b>{totals.total}</b>
            </Badge>
          </div>
        )}

        {loading ? (
          <Skeleton className="w-full h-64" />
        ) : !hasData ? (
          <div className="h-40 flex items-center justify-center text-sm text-muted-foreground border border-dashed border-border rounded-lg">
            {t('visits.empty', 'Nessuna visita registrata negli ultimi {{n}} giorni.', { n: days })}
          </div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={payload.data} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                <XAxis
                  dataKey="date"
                  tickFormatter={fmtDate}
                  tick={CHART_TICK_STYLE}
                  interval={days > 14 ? 3 : 0}
                />
                <YAxis
                  allowDecimals={false}
                  tick={CHART_TICK_STYLE}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: 11 }}
                  formatter={(v) => (SLUG_META[v]?.label || v)}
                />
                {slugs.map((slug) => (
                  <Line
                    key={slug}
                    type="monotone"
                    dataKey={slug}
                    stroke={SLUG_META[slug]?.color || '#666'}
                    strokeWidth={2.5}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
